import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { API_V1_BASE_URL } from '@/config/env';
import { getAccessToken, hasSessionHint } from '@/utils/auth';
import {
  getApiErrorMessage,
  isCoHostAccessRevokedError,
} from '@/utils/apiErrorMessage';

export interface CustomAxiosConfig extends InternalAxiosRequestConfig {
  _skipAuthInterceptor?: boolean;
  _skipCoHostRevokedHandler?: boolean;
  _retry?: boolean;
}

const api = axios.create({
  baseURL: API_V1_BASE_URL,
  withCredentials: true,
});

let refreshPromise: Promise<string | null> | null = null;
let onAuthFailure: (() => void) | null = null;
let onCoHostRevoked: ((message: string) => void) | null = null;

export const setAuthFailureHandler = (handler: () => void) => {
  onAuthFailure = handler;
};

export const setCoHostRevokedHandler = (handler: (message: string) => void) => {
  onCoHostRevoked = handler;
};

const shouldSkipCoHostRevoked = (config?: CustomAxiosConfig): boolean => {
  if (!config) return false;
  if (config._skipCoHostRevokedHandler) return true;
  const url = config.url ?? '';
  return (
    url.includes('/auth/exit-community') ||
    url.includes('/auth/logout') ||
    url.includes('/auth/refresh')
  );
};

const notifyCoHostRevoked = (error: AxiosError) => {
  const message = getApiErrorMessage(error, 'Akses co-host kamu telah dicabut');
  onCoHostRevoked?.(message);
};

export const setAuthHeader = (token: string | null) => {
  if (token) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common.Authorization;
  }
};

const getRefreshPromise = (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = import('@/services/authService')
      .then(({ authService }) => authService.refresh())
      .then((data) => data.token)
      .catch((error: unknown) => {
        if (isCoHostAccessRevokedError(error)) {
          notifyCoHostRevoked(error as AxiosError);
          return null;
        }
        onAuthFailure?.();
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

api.interceptors.request.use((config) => {
  const customConfig = config as CustomAxiosConfig;
  if (!customConfig._skipAuthInterceptor) {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosConfig | undefined;

    if (
      error.response?.status === 403 &&
      isCoHostAccessRevokedError(error) &&
      !shouldSkipCoHostRevoked(originalRequest)
    ) {
      notifyCoHostRevoked(error);
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._skipAuthInterceptor &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      // Guest (no session): do not refresh / clear session / kick to home
      if (!hasSessionHint() && !getAccessToken()) {
        return Promise.reject(error);
      }
      const newToken = await getRefreshPromise();
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
